import React from 'react';
import { nanoid } from '@reduxjs/toolkit';
import { batch } from 'react-redux';
import cx from 'classnames';

import { IClinicServicesOption } from '../../BookNowPanel';
import { MYSELF_AND_OTHERS_OPTION, ModeOption } from '../../utils/constants';
import {
  STEPS,
  popFamilyStep,
  pushFamilyStep,
  resetPatientSelections,
  updateFamilyBookingSessionId,
  updateSelectedPatients,
} from 'redux/familyBookingSlice';
import { useAppDispatch, useAppSelector } from 'redux/reduxHooks';
import { checkIsMatchingPatientRecord } from 'utils/checkIsSamePatient';
import useIsLoadedInsideIframe from 'hooks/useIsLoadedInsideIframe';

import NavigationController from '../NavigationController/NavigationController';
import {
  isPatientBookable,
  isSelectedServiceUnavailable,
} from '../utils/utils';
import AddPatient from './AddPatient/AddPatient';
import PatientRow from './PatientRow/PatientRow';
import styles from './PatientSchedule.module.scss';

interface PatientScheduleProps {
  mode: ModeOption;
  clinicServicesOptions: IClinicServicesOption[];
}

const PatientSchedule = ({
  mode,
  clinicServicesOptions,
}: PatientScheduleProps) => {
  const { contactUser, patients } = useAppSelector(
    (state) => state.familyBookingSlice
  );

  const dispatch = useAppDispatch();

  const isLoadedInsideIframe = useIsLoadedInsideIframe();

  const isPointOfContactIncluded = mode.id === MYSELF_AND_OTHERS_OPTION.id;

  const otherPatients = patients.filter(
    (patient) => !checkIsMatchingPatientRecord(patient, contactUser)
  );

  //--Slot selection reserves availability before review re-checks bookability,
  //--so an unbookable patient must never reach it even if a serviceId lingers
  const scheduledPatients = otherPatients.filter(
    (patient) => !!patient.serviceId && isPatientBookable(patient)
  );

  const hasScheduledPatient = scheduledPatients.length > 0;

  const isEveryScheduledServiceAvailable = scheduledPatients.every(
    (patient) => !isSelectedServiceUnavailable(patient)
  );

  const isContactUserScheduled =
    !!contactUser.serviceId && !isSelectedServiceUnavailable(contactUser);

  const isContactUserReady =
    !isPointOfContactIncluded ||
    (isPatientBookable(contactUser) && isContactUserScheduled);

  const isNextEnabled =
    hasScheduledPatient &&
    isEveryScheduledServiceAvailable &&
    isContactUserReady;

  const handleNextClicked = () => {
    const selectedPatients = isPointOfContactIncluded
      ? [contactUser, ...scheduledPatients]
      : scheduledPatients;

    batch(() => {
      dispatch(updateSelectedPatients(selectedPatients));
      dispatch(updateFamilyBookingSessionId(nanoid()));
      dispatch(pushFamilyStep(STEPS.SLOT_SELECTION));
    });
  };

  const handleBackClicked = () => {
    batch(() => {
      dispatch(resetPatientSelections());
      dispatch(popFamilyStep());
    });
  };

  return (
    <div
      className={cx(styles['container'], {
        [styles['container-iframe']]: isLoadedInsideIframe,
      })}
    >
      <PatientRow
        key={mode.id}
        isPointOfContact
        patient={contactUser}
        mode={mode}
        services={clinicServicesOptions}
      />

      <div className={styles['patient-list-section']}>
        <div className={styles['title']}>Schedule for others</div>

        {otherPatients.length > 0 && (
          <div className={styles['patient-list']}>
            {otherPatients.map((patient) => (
              <PatientRow
                key={
                  patient.id ||
                  `${patient.firstName}-${patient.lastName}-${patient.dob}`
                }
                patient={patient}
                mode={mode}
                services={clinicServicesOptions}
              />
            ))}
          </div>
        )}

        <AddPatient
          mode={mode}
          hasPatientOnFile={patients.some(isPatientBookable)}
        />
      </div>

      <NavigationController
        isDisabledSubmit={!isNextEnabled}
        title="Next"
        onClickBack={handleBackClicked}
        onClickNext={handleNextClicked}
      />
    </div>
  );
};

export default PatientSchedule;
